
// ---------------------------------------------------------------------------------------------------------------
// main.js — application entry: wires the page chrome to the subsystems and runs the fixed-step game loop.
//
// Public names: main (runs once the DOM is ready), window.__pin, window.render_game_to_text, window.advanceTime.
// Depends on: DT (physics.js), buildTable (table.js), Lamps + Game (rules.js), Renderer (renderer.js),
//             DMD (dmd.js), AudioEngine (audio.js), Input (input.js), DebugOverlay (debug.js).
//
// Responsibilities: construct table/rules/audio/renderer/DMD/debug/input; the ONE pause owner setPaused(v, reason)
// that every path (P key, button, overlay click, blur, restart, machine viewer, error) goes through and that keeps
// #btnPause/overlay/hint in sync; mute + help (focus-managed dialog) + restart UI; the controls table with runtime
// rebinding (validated, persisted in localStorage); the machine viewer toggle (renderer.setMachineView); window
// resize; splash dismissal (capture phase, so the dismissing input never reaches the game); routing Game.fx events
// to audio / DMD / HUD / renderer with change-only DOM writes; the render-interpolation snapshot; the hint line;
// the 120 Hz fixed-step loop with a 6-step catch-up budget, a guarded step (error banner instead of a frozen
// picture) and rate-limited subsystem error logging; and the deterministic test hooks used by the harness.
// ---------------------------------------------------------------------------------------------------------------

/** localStorage key holding remapped key bindings: { action: [KeyboardEvent.code, ...] }. */
const BINDINGS_STORAGE_KEY = "tokenStormKeyBindingsZenX_v2";

/** Rows of the controls table, in display order: [action, label]. */
const BINDING_TABLE_ROWS = [
  ["flipL", "Left lower flipper"],
  ["flipR", "Right lower flipper"],
  ["plunge", "Plunger (hold + release)"],
  ["start", "Start"],
  ["nudgeL", "Nudge left"],
  ["nudgeR", "Nudge right"],
  ["nudgeU", "Nudge up"],
];

/** Hint-line texts per situation (refreshed on every state change and polled twice a second). */
const HINT_TEXT = {
  paused: "PAUSED — press P to resume",
  ATTRACT: "Press 1 — or click — to start · H for controls",
  LAUNCH: "Hold ENTER (or press-hold right edge) and release to launch",
  multiball: "TOKEN STORM! Shoot the flashing ramp for JACKPOT",
  PLAY: "Lower: Shift keys · Hold lower 0.5s for stage 2",
  INITIALS: "Flippers change letter · START confirms",
  machine: "VIEW MACHINE — drag to orbit · wheel / pinch to zoom · Esc to return",
  halted: "GAME HALTED — see the message on screen",
};
// Phones and tablets have no Shift keys or space bar: describe the touch zones instead.
if (typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches) {
  HINT_TEXT.ATTRACT = "Tap the table to start · ⌨ for controls";
  HINT_TEXT.LAUNCH = "Press and hold the right edge, release to launch";
  HINT_TEXT.PLAY = "Hold the left / right half of the screen to flip";
}

/**
 * Longest real-time gap fed to the simulation per animation frame (tab switches, debugger pauses...). Deltas are
 * also clamped at 0: some RAF clocks (headless browsers) run behind performance.now() and a negative dt would
 * blow up the camera shake and frame-time smoothing.
 */
const MAX_FRAME_SECONDS = 0.25;
/**
 * Physics steps allowed per animation frame. When a frame needs more than this (a device below ~20 fps, a long
 * hitch) the remaining backlog is DROPPED, not deferred: the simulation then covers less time than the wall clock
 * (a hitch costs a fraction of a second of play) instead of entering a spiral where every frame has more to catch
 * up than the previous one. The visible effect is a short skip, never a permanent slow-down.
 */
const MAX_CATCHUP_STEPS = 6;
/** Minimum gap between two console reports of the same failing subsystem (fx / input / render). */
const ERROR_REPORT_INTERVAL_MS = 5000;

/** Shorthand for document.querySelector. */
const dom = (selector) => document.querySelector(selector);

/**
 * Hint text for the current situation, or null when the previous hint should simply stay on screen
 * (OVER, TILT and other states never overwrite the hint).
 */
function hintForState(state, paused, multiball) {
  if (paused) return HINT_TEXT.paused;
  if (state === "ATTRACT") return HINT_TEXT.ATTRACT;
  if (state === "LAUNCH") return HINT_TEXT.LAUNCH;
  if (state === "PLAY") return multiball ? HINT_TEXT.multiball : HINT_TEXT.PLAY;
  if (state === "INITIALS") return HINT_TEXT.INITIALS;
  return null;
}

/** "KeyZ" -> "Z", "Digit3" -> "3", "ArrowLeft" -> "Left"; other codes are shown verbatim. */
function keyCodeLabel(code) {
  return code.replace("Key", "").replace("Digit", "").replace("Arrow", "");
}

/**
 * Replace `binds` in place with the saved bindings validated exactly like a live rebind (Input.sanitizeBindings:
 * well-formed entries only, no reserved codes, no key on two actions, four slots). Missing/corrupt storage and
 * unknown actions leave the defaults.
 */
function loadSavedBindings(binds) {
  let saved = null;
  try {
    saved = JSON.parse(localStorage.getItem(BINDINGS_STORAGE_KEY) || "null");
  } catch (err) {
    console.warn("Saved key bindings could not be loaded:", err);
  }
  Object.assign(binds, Input.sanitizeBindings(saved));
}

/** Persist the bindings object; storage may be unavailable (private mode, quota), which is not fatal. */
function saveBindings(binds) {
  try {
    localStorage.setItem(BINDINGS_STORAGE_KEY, JSON.stringify(binds));
  } catch (err) {
    console.warn("Key bindings could not be saved:", err);
  }
}

/** Swallow the rejection of an optional promise (fullscreen requests fail on some browsers/gestures). */
function ignoreRejection(maybePromise) {
  if (maybePromise && typeof maybePromise.catch === "function") maybePromise.catch(() => {});
}

async function main() {
  GameLoading.stage(10, "Building your table…");
  await GameLoading.paint();
  const canvas = dom("#game");
  const table = buildTable();
  const world = table.world;
  const lamps = new Lamps(table.lamps);
  const game = new Game(table, lamps);
  const embeddedDemo = window.parent !== window;
  let hostVisible = !embeddedDemo, gameFrame = 0;
  game.continuousDemo = embeddedDemo;
  if (embeddedDemo) document.documentElement.classList.add("embedded-demo");
  const audio = new AudioEngine();
  audio.setMuted(true); // Local scroll-loaded demo is silent until the player explicitly unmutes.
  GameLoading.stage(30, "Setting up the arcade…");
  await GameLoading.paint();
  let renderer;
  try {
    renderer = new Renderer(canvas, table, lamps);
    await renderer.initialize();
  } catch (err) {
    dom("#nogl").classList.add("show");
    GameLoading.fail(err);
    return;
  }
  const dmd = new DMD(renderer.dmdCanvas);
  const debugOverlay = new DebugOverlay(table, renderer);

  // ---- page chrome ---------------------------------------------------------------------------------------------
  const hintEl = dom("#hint");
  const scoreEl = dom("#score");
  const ballNumEl = dom("#ballnum");
  const pauseOverlay = dom("#overlay");
  const helpPanel = dom("#help");
  const helpCloseButton = dom("#helpClose");
  const bindRowsEl = dom("#bindRows");
  const pauseButton = dom("#btnPause");
  const muteButton = dom("#btnMute");
  const helpButton = dom("#btnHelp");
  const viewButton = dom("#btnView");
  const fullscreenButton = dom("#btnFull");
  const machineButton = dom("#btnMachine");
  const restartButton = dom("#btnRestart");
  const errorBanner = dom("#errorBanner");

  let paused = false;
  let pauseReason = null;
  let loopHalted = false; // set by haltWithError: the loop stopped and the error banner is up
  let helpOpen = false;
  let helpOpener = null; // element focused when the help panel opened; focus returns there on close
  const machineView = { active: false, prior: null }; // prior = { paused, debug } to restore on exit
  let lastHint = null;

  /** Write textContent only when it changes: the HUD/hint are updated 120x per second otherwise. */
  function setText(element, text) {
    if (element.textContent !== text) element.textContent = text;
  }

  /** Hint for the current situation (machine viewer and a halted loop outrank the game state). */
  function refreshHint() {
    let text;
    if (loopHalted) text = HINT_TEXT.halted;
    else if (machineView.active) text = HINT_TEXT.machine;
    else text = hintForState(game.state, paused, game.multiball);
    if (text === null || text === lastHint) return;
    lastHint = text;
    hintEl.textContent = text;
  }

  /** Pause button glyph/title/ARIA and the overlay follow the pause flag (the overlay hides in the viewer). */
  let pauseFocusReturn = null;
  function syncPauseUI() {
    const showOverlay = !embeddedDemo && paused && !machineView.active && !loopHalted;
    const wasShown = pauseOverlay.style.display === "grid";
    pauseOverlay.style.display = showOverlay ? "grid" : "none";
    if (showOverlay && !wasShown) {
      // Move keyboard focus into the dialog container (not onto RESTART GAME: Enter/Space are plunger keys and
      // must not restart a paused game). Tab reaches the button; Escape or P resumes. Remember where focus was.
      pauseFocusReturn = document.activeElement;
      pauseOverlay.focus?.({ preventScroll: true });
    } else if (!showOverlay && wasShown) {
      // Only hand focus back when it is still inside the dialog; a click on a toolbar button already moved it.
      const back = pauseFocusReturn;
      pauseFocusReturn = null;
      const active = document.activeElement;
      if (active && pauseOverlay.contains(active) && back && document.contains(back) && back !== document.body) {
        back.focus?.({ preventScroll: true });
      }
    }
    pauseButton.querySelector("use").setAttribute("href", paused ? "#icon-play" : "#icon-pause");
    pauseButton.querySelector(".button-label").textContent = paused ? "Resume" : "Pause";
    pauseButton.title = paused ? "Resume (P)" : "Pause (P)";
    pauseButton.setAttribute("aria-label", paused ? "Resume game" : "Pause game");
    pauseButton.setAttribute("aria-pressed", String(paused));
  }

  /**
   * The single pause owner. `reason` documents the caller ('key', 'button', 'overlay', 'blur', 'restart',
   * 'machine', 'error', 'api') and is only used for tracing. Held inputs are released when the game becomes
   * paused (a key held across the boundary must not stay "down"); resuming releases nothing so a flipper held
   * through advanceTime stays held. Nothing can resume a halted loop.
   */
  function setPaused(value, reason = "api") {
    const next = (!embeddedDemo && Boolean(value)) || loopHalted;
    if (next !== paused) {
      paused = next;
      pauseReason = next ? reason : null;
      if (paused) input.releaseAll("pause:" + reason);
    }
    syncPauseUI();
    refreshHint();
  }

  // Local preview visibility is independent of explicit Pause; it never rewrites paused or pauseReason.
  function syncHostVisibility() {
    const visible = hostVisible && !document.hidden;
    renderer.hostSuspended = !visible;
    if (!visible) {
      cancelAnimationFrame(gameFrame); gameFrame = 0;
      cancelAnimationFrame(renderer.machineRaf); renderer.machineRaf = 0;
      input.releaseAll('host-hidden');
      if (window.__pin) window.__pin.fps = 0;
      return;
    }
    lastFrameAt = performance.now(); accumulator = 0;
    if (entered && !loopHalted && !gameFrame) gameFrame = requestAnimationFrame(frame);
    if (machineView.active && !renderer.machineRaf) renderer.machineRaf = requestAnimationFrame(renderer.machineLoop);
  }

  /** Mute button mirrors the audio engine flag (the M key and the button share this path). */
  function syncMuteButton() {
    const muted = !audio.enabled;
    muteButton.querySelector("use").setAttribute("href", muted ? "#icon-muted" : "#icon-sound");
    muteButton.querySelector(".button-label").textContent = muted ? "Muted" : "Sound";
    muteButton.setAttribute("aria-pressed", String(muted));
    muteButton.setAttribute("aria-label", muted ? "Unmute sound" : "Mute sound");
  }

  /** Machine button state; the button only exists for players when the renderer implements the viewer. */
  function syncMachineButton() {
    machineButton.setAttribute("aria-pressed", String(machineView.active));
    machineButton.setAttribute("aria-label", machineView.active ? "Exit View Machine" : "Enter View Machine 3D viewer");
    machineButton.title = machineView.active ? "Back to the game (Esc)" : "View complete machine in 3D";
    // The viewer is modal: pause/camera/help would fight the orbit camera or unpause the hidden game.
    for (const button of [pauseButton, viewButton, helpButton]) button.disabled = machineView.active;
  }

  /** Show/hide the controls dialog: focus moves to the close button and returns to the opener afterwards. */
  function toggleHelp(force) {
    if (embeddedDemo) return;
    const show = force ?? !helpOpen;
    if (show === helpOpen) return;
    helpOpen = show;
    helpPanel.style.display = show ? "block" : "none";
    helpButton.setAttribute("aria-expanded", String(show));
    if (show) {
      const active = document.activeElement;
      helpOpener = active && active !== document.body ? active : null;
      input.releaseAll("help");
      renderBindingRows();
      helpPanel.scrollTop = 0;
      helpCloseButton.focus();
    } else {
      input.cancelCapture(); // a pending rebind must not swallow (and persist) the next game key
      if (helpOpener && document.contains(helpOpener)) helpOpener.focus();
      else document.activeElement?.blur?.();
      helpOpener = null;
    }
  }

  /** Give every rebind button an accessible name derived from its row ("Rebind Left flipper"). */
  function labelRebindButtons() {
    document.querySelectorAll(".rebind").forEach((button) => {
      const rowName = button.closest("tr")?.cells[0]?.textContent?.trim() || button.dataset.act;
      button.setAttribute("aria-label", `Rebind ${rowName}`);
    });
  }

  const rebindButton = (action) => document.querySelector(`.rebind[data-act="${action}"]`);

  /** Regenerate the controls table from the live bindings; a rebind button arms Input.captureFor. */
  function renderBindingRows() {
    bindRowsEl.innerHTML = BINDING_TABLE_ROWS.map(([action, label]) => {
      const keys = input.binds[action].map((code) => `<kbd>${keyCodeLabel(code)}</kbd>`).join(" ") || "—";
      return `
      <tr><td>${label}</td>
      <td>${keys}</td>
      <td><button class="rebind" data-act="${action}">rebind</button></td></tr>`;
    }).join("");
    document.querySelectorAll(".rebind").forEach((button) => {
      button.onclick = () => armRebind(button.dataset.act);
    });
    labelRebindButtons();
  }

  /** Arm a remap for `action` (any other armed row is reset) and keep focus on its button. */
  function armRebind(action) {
    renderBindingRows();
    input.beginCapture(action);
    const button = rebindButton(action);
    button.textContent = "press a key…";
    button.focus();
  }

  /** Enter/leave the renderer's machine viewer (a no-op when the renderer does not implement it). */
  function setMachineView(active) {
    if (embeddedDemo) return false;
    if (typeof renderer.setMachineView !== "function") return false;
    const next = Boolean(active);
    if (next === machineView.active) return true;
    if (next) {
      if (helpOpen) toggleHelp(false);
      machineView.prior = { paused, debug: debugOverlay.enabled };
      if (debugOverlay.enabled) debugOverlay.toggle(); // toggle() clears its canvas: no stale wireframe in the showroom
      machineView.active = true;
      input.releaseAll("machine");
      setPaused(true, "machine");
      renderer.setMachineView(true);
    } else {
      machineView.active = false;
      renderer.setMachineView(false);
      if (machineView.prior.debug && !debugOverlay.enabled) debugOverlay.toggle();
      setPaused(machineView.prior.paused, "machine");
      machineView.prior = null;
    }
    syncMachineButton();
    refreshHint();
    return true;
  }

  /** The step loop threw: stop the loop, keep the game paused and say so instead of showing a frozen picture. */
  function haltWithError(err) {
    console.error("Game loop halted:", err);
    loopHalted = true;
    dom("#errorText").textContent = String((err && err.message) || err);
    errorBanner.classList.add("show");
    setPaused(true, "error");
  }

  // ---- splash (registered BEFORE the Input listeners so the dismissing input never reaches the game) -----------
  const splash = dom("#splash");
  let entered = false;
  const dismissSplash = (event, automatic = false) => {
    if (!GameLoading.ready || entered) return;
    if (!automatic && event.type === 'keydown' && (event.key === 'Tab' || event.repeat || event.ctrlKey || event.metaKey || event.altKey)) return;
    event?.stopImmediatePropagation();
    event?.preventDefault();
    entered = true;
    input.releaseAll('startup');
    if (!automatic) { audio.ensure(); audio.resume(); }
    document.querySelector('#gameUI').inert = false;
    document.querySelector('#gameUI').removeAttribute('aria-hidden');
    splash.remove();
    lastFrameAt = performance.now();
    accumulator = 0;
    bridge.sendStatus();
    syncHostVisibility();
    removeEventListener('keydown', dismissSplash, {capture: true});
  };
  dom('#btnEnter').addEventListener('click', dismissSplash);
  addEventListener('keydown', dismissSplash, {capture: true});

  // ---- input -----------------------------------------------------------------------------------------------------
  const hooks = {
    onPause: () => setPaused(!paused, "key"),
    onHostVisibility: visible => { if (hostVisible !== visible) { hostVisible = visible; syncHostVisibility(); } },
    isHostVisible: () => hostVisible && !document.hidden,
    onHostFocus: () => { if (paused && pauseReason === "blur" && !document.hidden && entered) setPaused(false, "host-control"); },
    onDebug: () => debugOverlay.toggle(),
    onRestart: () => game.onRestart(),
    onView: () => renderer.cycleView(),
    onMute: () => {
      audio.setMuted(audio.enabled);
      syncMuteButton();
    },
    onHelp: () => toggleHelp(),
    onFullscreen: () => {
      if (!fullscreenSupported) return;
      if (document.fullscreenElement) ignoreRejection(document.exitFullscreen());
      else ignoreRejection(document.documentElement.requestFullscreen());
    },
    onAnyInput: () => {
      // Browsers only allow audio after a user gesture: create/resume the context on the first real input.
      audio.ensure();
      audio.resume();
    },
    onRemapped: (action, outcome) => {
      if (outcome === "reserved") {
        const button = rebindButton(action);
        if (button) button.textContent = "reserved key — try another";
        return; // capture stays armed
      }
      if (outcome === "bound") saveBindings(input.binds);
      renderBindingRows();
      rebindButton(action)?.focus();
    },
    onEscape: () => {
      if (helpOpen) {
        toggleHelp(false);
        return true;
      }
      if (machineView.active) return setMachineView(false);
      return false;
    },
    inputBlocked: () => (!hostVisible || document.hidden ? "host-hidden" : !entered ? "loading" : machineView.active ? "machine" : helpOpen ? "help" : false),
    isPaused: () => paused,
    hostCanResume: () => paused && pauseReason === "blur" && !document.hidden && entered,
  };
  const input = new Input(game, hooks);
  loadSavedBindings(input.binds);
  input.binds.flipU = []; // Upper stage is hold/demo driven, never a keyboard shortcut.
  const bridge = new PinballBridge(input);
  input.hooks.onControlState = () => bridge.sendStatus();

  // Fullscreen is feature-detected (iPhone Safari has no element fullscreen): no button when it cannot work.
  const fullscreenSupported = Boolean(document.fullscreenEnabled && document.documentElement.requestFullscreen);
  fullscreenButton.hidden = !fullscreenSupported;
  machineButton.hidden = typeof renderer.setMachineView !== "function";

  // Buttons go through input.hooks (not the local object), so a hook replaced on the Input instance is honoured by
  // the on-screen buttons as well. A mouse/touch click drops focus again (click.detail is 0 for keyboard
  // activation) so Space/Enter go back to the game instead of re-activating the last clicked button.
  for (const button of document.querySelectorAll("#buttons button, #btnRestart, #btnReload")) {
    button.addEventListener("click", (event) => {
      if (event.detail > 0) button.blur();
    });
  }
  muteButton.onclick = () => input.hooks.onMute();
  viewButton.onclick = () => input.hooks.onView();
  helpButton.onclick = () => toggleHelp();
  fullscreenButton.onclick = () => input.hooks.onFullscreen();
  machineButton.onclick = () => setMachineView(!machineView.active);
  helpCloseButton.onclick = () => toggleHelp(false);
  dom("#btnResume").onclick = () => setPaused(false, "resume-button");
  pauseButton.onclick = () => setPaused(!paused, "button");
  pauseOverlay.addEventListener("click", (event) => {
    if (!event.target.closest("button")) setPaused(false, "overlay"); // click anywhere on the pause card resumes
  });
  restartButton.onclick = () => {
    setPaused(false, "restart");
    game.startGame();
  };
  dom("#btnReload").onclick = () => location.reload();
  syncMuteButton();
  syncMachineButton();

  // ---- window events ---------------------------------------------------------------------------------------------
  const onResize = () => renderer.resize(innerWidth, innerHeight);
  addEventListener("resize", onResize);
  onResize();
  const onFocusLost = () => {
    // Losing focus mid-ball pauses (the ball would otherwise drain unattended, or launch itself: Input cancels a
    // plunger pull first); menus/attract keep running; the viewer is already paused.
    if ((game.state === "PLAY" || game.state === "LAUNCH") && !machineView.active) setPaused(true, "blur");
  };
  addEventListener("blur", onFocusLost);
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) onFocusLost();
    syncHostVisibility();
  });

  // ---- Game.fx -> audio / DMD / HUD / renderer ------------------------------------------------------------------
  let lastStatus = null; // last 'score' fx applied to the DMD/HUD (rules may emit one per step; only changes matter)

  /** Apply a 'score' status fx when any of its fields changed since the last one. */
  function applyStatus(fx) {
    if (
      lastStatus &&
      lastStatus.score === fx.score && lastStatus.ball === fx.ball && lastStatus.state === fx.state &&
      lastStatus.ballSave === fx.ballSave && lastStatus.bonusX === fx.bonusX && lastStatus.power === fx.power
    ) return;
    lastStatus = {
      score: fx.score, ball: fx.ball, state: fx.state, ballSave: fx.ballSave, bonusX: fx.bonusX, power: fx.power,
    };
    dmd.setStatus(fx);
    setText(scoreEl, fx.score.toLocaleString("en-US"));
    setText(dom("#bestScore"), Math.max(fx.score, ...game.hiscores.map(row => row.score)).toLocaleString("en-US"));
    setText(
      ballNumEl,
      fx.state === "ATTRACT" || fx.state === "OVER"
        ? "PRESS START"
        : "BALL " + fx.ball + (fx.bonusX > 1 ? " · " + fx.bonusX + "X" : ""),
    );
  }

  /** Route queued fx; `dropSounds` is used by advanceTime, where hundreds of cues would burst out at once. */
  function dispatchFx(dropSounds = false) {
    for (const fx of game.fx) {
      switch (fx.type) {
        case "sound":
          if (!dropSounds) audio.play(fx.name, fx);
          break;
        case "dmd":
          dmd.handle(fx);
          break;
        case "score":
          applyStatus(fx);
          break;
        default:
          renderer.handleFx(fx);
      }
    }
    game.fx.length = 0;
  }

  // Previous-step positions/angles; the renderer blends snapshot -> current by the accumulator fraction so
  // 120 Hz physics looks smooth at any display rate.
  const snapshot = { balls: new Map(), flippers: { L: 0, R: 0 } };
  function takeSnapshot() {
    snapshot.balls.clear();
    for (const ball of world.balls) snapshot.balls.set(ball.id, { x: ball.p.x, y: ball.p.y, z: ball.z });
    snapshot.flippers.L = table.meta.flippers.L.angle;
    snapshot.flippers.R = table.meta.flippers.R.angle;
    if (table.meta.flippers.U) snapshot.flippers.U = table.meta.flippers.U.angle;
  }

  let simTime = 0; // total simulated seconds handed to Game.update (its clock for timers); shared with advanceTime

  /** One fixed physics + rules step (the only place the world and the game clock advance). */
  function stepOnce() {
    takeSnapshot();
    world.step();
    simTime += DT;
    game.update(DT, simTime);
  }

  setInterval(refreshHint, 500); // catches state changes that happen inside the rules (drain, bonus, tilt...)
  refreshHint();

  // ---- test / automation hooks ----------------------------------------------------------------------------------
  window.__pin = {
    game,
    T: table,
    renderer,
    input,
    overlay: debugOverlay,
    fps: 0,
    isPaused: () => paused,
    isHostVisible: () => hostVisible && !document.hidden,
    hostCanResume: () => paused && pauseReason === "blur" && !document.hidden && entered,
    setPaused: (value) => setPaused(value, "api"),
    setMachineView,
    isMachineView: () => machineView.active,
    simTime: () => simTime,
  };

  const round4 = (value) => (Number.isFinite(value) ? Number(value.toFixed(4)) : 0);

  /** Compact JSON description of the live game state (positions in playfield metres, 4 decimals). */
  window.render_game_to_text = () => {
    const balls = world.balls
      .filter((ball) => ball.active)
      .map((ball) => ({
        id: ball.id,
        x: round4(ball.p.x),
        y: round4(ball.p.y),
        z: round4(ball.z),
        vx: round4(ball.v.x),
        vy: round4(ball.v.y),
        held: Boolean(ball.held),
        layer: ball.layer,
      }));
    const flippers = {};
    for (const flipper of world.flippers) {
      flippers[flipper.side] = { pressed: Boolean(flipper.pressed), angle: round4(flipper.angle) };
    }
    return JSON.stringify({
      coordinateSystem: "Playfield meters: origin at lower-left; +x right; +y toward top; +z above table.",
      mode: game.state,
      modeTitle: game.mode?.title || null, // Game has no mode object today; kept for the report shape
      score: game.score || 0,
      ball: game.ballNum || 0,
      balls,
      flippers,
      plunger: { power: round4(game.plungePower), returning: Boolean(game.plungerStroke), seated: game.hasSeatBall() },
      mechanisms: {
        save: Object.fromEntries(['L','R'].map(side=>[side,{armed:game['kickbackArmed'+side],
          dwell:round4(Math.max(0,...table.world.balls.filter(b=>b.active&&Math.abs(b.p.x-(side==='L'?.0195:.431))<.008).map(b=>b._kickT||0)))}])),
        achievement: renderer.arcadeCelebration&&table.world.time-renderer.arcadeCelebration.started<renderer.arcadeCelebration.duration?renderer.arcadeCelebration.label:null,
      },
      paused,
      viewMachine: machineView.active,
      controls: JSON.parse(JSON.stringify(input.binds)),
    });
  };

  /**
   * Deterministically advance the simulation by `milliseconds` of fixed 120 Hz steps. The loop runs synchronously
   * (requestAnimationFrame cannot interleave), so the pause state is left alone: held flippers stay held. The
   * queued fx are drained into the DMD/HUD/renderer (sounds dropped) and the loop clock/snapshot advance exactly
   * as they would in the live loop.
   */
  window.advanceTime = (milliseconds) => {
    const requested = Number(milliseconds);
    if (!Number.isFinite(requested) || requested < 0) {
      throw new TypeError("advanceTime(ms) requires a non-negative finite number");
    }
    const steps = Math.round(requested / (1000 / 120));
    for (let step = 0; step < steps; step++) stepOnce();
    dispatchFx(true);
    return window.render_game_to_text();
  };

  // ---- main loop -------------------------------------------------------------------------------------------------
  let accumulator = 0; // real seconds not yet simulated
  let lastFrameAt = performance.now();
  let fpsFrames = 0;
  let fpsElapsed = 0;
  const lastReportAt = {};

  /** Log a subsystem failure at most once per ERROR_REPORT_INTERVAL_MS; the other subsystems keep running. */
  function reportSubsystemError(name, err) {
    const now = performance.now();
    if (lastReportAt[name] && now - lastReportAt[name] < ERROR_REPORT_INTERVAL_MS) return;
    lastReportAt[name] = now;
    console.warn('subsystem "' + name + '" error (further reports muted for 5 s):', err);
  }

  function frame(now) {
    gameFrame = 0;
    if (loopHalted || !hostVisible || document.hidden) return;
    gameFrame = requestAnimationFrame(frame);
    let dt = (now - lastFrameAt) / 1e3;
    lastFrameAt = now;
    dt = Math.min(MAX_FRAME_SECONDS, Math.max(0, dt));
    fpsFrames++;
    fpsElapsed += dt;
    if (fpsElapsed >= 0.5) {
      window.__pin.fps = Math.round(fpsFrames / fpsElapsed);
      fpsFrames = 0;
      fpsElapsed = 0;
    }
    if (paused) return; // paused: no physics, no fx drain, no redraw (the overlay covers the last frame)

    accumulator += dt;
    let steps = 0;
    try {
      while (accumulator >= DT && steps < MAX_CATCHUP_STEPS) {
        stepOnce();
        steps++;
        accumulator -= DT;
      }
    } catch (err) {
      haltWithError(err);
      return;
    }
    if (steps === MAX_CATCHUP_STEPS) accumulator = 0; // over budget: drop the backlog (see MAX_CATCHUP_STEPS)

    try {
      input.pollGamepad();
    } catch (err) {
      reportSubsystemError("input", err);
    }
    try {
      dispatchFx();
    } catch (err) {
      reportSubsystemError("fx", err);
    }
    const alpha = accumulator / DT; // 0..1 interpolation fraction between snapshot and current state
    try {
      dmd.draw(dt);
      renderer.dmdTex.needsUpdate = true;
      renderer.render(dt, world, game, alpha, snapshot);
      debugOverlay.draw(world);
    } catch (err) {
      reportSubsystemError("render", err);
    }
  }
  await renderer.prepareForPlay(world, game, dmd, snapshot, GameLoading.stage);
  GameLoading.complete();
  dismissSplash(null, true); // Ready attract mode is interactive immediately; no Enter gate or automatic ball start.
  bridge.sendStatus();
}

const startApplication = () => main().catch(GameLoading.fail);
if (document.readyState === "loading") addEventListener("DOMContentLoaded", startApplication, {once: true});
else startApplication();
